if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      constructor() {
        super();
        document.body.classList.add('sticky-atc-bar-enabled');

        this.selectors = {
          variantIdSelect: '[name="id"]',
        };
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      get quantity() {
        return this.querySelector('quantity-input');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get select() {
        return this.querySelector('select');
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      connectedCallback() {
        this.productFormActions = document.querySelector('.main-product-form');
        this.mainProductInfo = document.querySelector('product-info');
        this.container = this.closest('.sticky-atc-bar');

        this.variantData = this.getVariantData();
        
        // Ensure selling plan data is available
        this.ensureSellingPlanData();

        this.init();

        this.select.addEventListener('change', () => {
          this.updateQuantityInput();
          this.mainVariantSelects = this.mainProductInfo && this.mainProductInfo.querySelector('variant-selects');
          const selectedVariantId = this.querySelector(this.selectors.variantIdSelect).value;
          this.currentVariant = this.variantData.find((variant) => variant.id === Number(selectedVariantId));

          if (this.mainVariantSelects) {
            Array.from(this.mainVariantSelects.querySelectorAll('select, fieldset'), (element, index) => {
              const variantOptionVal = this.currentVariant.options[index];

              switch (element.tagName) {
                case 'SELECT':
                  element.value = variantOptionVal;
                  break;
                case 'FIELDSET':
                  Array.from(element.querySelectorAll('input')).forEach((radio) => {
                    if (radio.value === variantOptionVal) {
                      radio.checked = true;
                    }
                  });
                  break;
              }
            });
            this.mainVariantSelects.dispatchEvent(new Event('change'));
          }

          this.updatePrice();
          this.updateButton(true, '', false);
          if (!this.currentVariant) {
            this.updateButton(true, '', true);
          } else {
            this.updateButton(!this.currentVariant.available, FoxTheme.variantStrings.soldOut);
          }
        });
      }
      
      /**
       * Ensures that selling plan allocation data is available
       * This either confirms the data exists or attempts to create it
       */
      ensureSellingPlanData() {
        if (window.variantSellingPlanAllocations) {
          console.debug('[sticky-atc] Selling plan allocation data already available');
          return;
        }
        
        console.debug('[sticky-atc] Attempting to create selling plan allocation data');
        
        try {
          // Check for existing element
          const existingElement = document.getElementById('variant-selling-plan-allocations');
          if (existingElement) {
            try {
              // Try to parse the existing element
              const contentText = existingElement.textContent.trim();
              window.variantSellingPlanAllocations = JSON.parse(contentText);
              console.debug('[sticky-atc] Created window.variantSellingPlanAllocations from existing element');
              return;
            } catch (jsonError) {
              console.warn('[sticky-atc] Error parsing selling plan allocations JSON:', jsonError);
              // Continue to try other methods
            }
          }
          
          // Try to find product JSON data to extract selling plan allocations
          const productJsonScript = document.querySelector(
            '#ProductJson-product-template, ' +
            '[id^="ProductJson-"], ' +
            '#product-json, ' +
            '#shopify-product-json, ' +
            '#product-data'
          );
          
          if (!productJsonScript) {
            console.warn('[sticky-atc] No product JSON found to extract selling plan data');
            return;
          }
          
          // Parse the product data
          const productData = JSON.parse(productJsonScript.textContent);
          if (!productData || !productData.variants) {
            console.warn('[sticky-atc] Product data does not contain variants');
            return;
          }
          
          // Create selling plan allocation data structure
          const allocations = {};
          
          productData.variants.forEach(variant => {
            if (variant.selling_plan_allocations && variant.selling_plan_allocations.length > 0) {
              allocations[variant.id] = variant.selling_plan_allocations.map(allocation => ({
                selling_plan_id: allocation.selling_plan.id,
                price: allocation.price,
                compare_at_price: allocation.compare_at_price
              }));
            }
          });
          
          // Save to window
          window.variantSellingPlanAllocations = allocations;
          console.debug('[sticky-atc] Created selling plan allocation data from product JSON:', allocations);
        } catch (e) {
          console.error('[sticky-atc] Error creating selling plan allocation data:', e);
        }
      }

      updateQuantityInput() {
        const selectedVariantId = this.querySelector(this.selectors.variantIdSelect).value;
        this.currentVariant = this.variantData.find((variant) => variant.id === Number(selectedVariantId));
        fetch(`${this.getAttribute('data-url')}?variant=${this.currentVariant.id}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const quantity = document.querySelector(`#QuantitySticky-${this.sectionId}`);
            const newQuantity = parsedHTML.querySelector(`#QuantitySticky-${this.sectionId}`);
            if (newQuantity) {
              quantity.innerHTML = newQuantity.innerHTML;
            }
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {});
      }

      getVariantData() {
        this.variantData =
          this.variantData || JSON.parse(this.container.querySelector('[type="application/json"]').textContent);
        return this.variantData;
      }

      init() {
        if (!this.productFormActions) {
          this.container.classList.add('sticky-atc-bar--show');
          return;
        }

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.checkDevice.bind(this);
        this.checkDevice();

        const rootMargin = `-80px 0px 0px 0px`;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const method = entry.intersectionRatio !== 1 ? 'add' : 'remove';
              this.container.classList[method]('sticky-atc-bar--show');
            });
          },
          { threshold: 1, rootMargin }
        );
        this.setObserveTarget();
        this.syncWithMainProductForm();
        
        // Set up the observer for main product price changes (for Recharge)
        this.observeMainPrice();
        
        // Wait for all scripts to initialize before checking for selling plans
        // This gives Recharge time to set up its widget and modify the DOM
        window.addEventListener('load', () => {
          console.debug('[sticky-atc] Window load event - checking for selling plans');
          // Use requestAnimationFrame to ensure DOM is fully updated
          requestAnimationFrame(() => {
            setTimeout(() => {
              this.updatePriceFromSellingPlan();
            }, 500); // Give extra time for Recharge to fully initialize
          });
        });
        
        // Also check on DOMContentLoaded as a backup
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => {
            console.debug('[sticky-atc] DOMContentLoaded event - checking for selling plans');
            setTimeout(() => this.updatePriceFromSellingPlan(), 300);
          });
        } else {
          // If DOM is already loaded, check after a short delay
          setTimeout(() => this.updatePriceFromSellingPlan(), 300);
        }
      }

      setObserveTarget() {
        this.observer.observe(this.productFormActions);
        this.observeTarget = this.productFormActions;
      }

      checkDevice(e) {
        document.documentElement.style.setProperty('--sticky-atc-bar-height', this.clientHeight + 'px');
      }

      updateButton(disable = true, text, modifyClass = true) {
        const productForm = this.querySelector('#product-form-sticky-atc-bar');
        if (!productForm) return;

        const addButton = productForm.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector('span');
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = FoxTheme.variantStrings.addToCart;
        }
      }

      updatePrice() {
        const classes = {
          onSale: 'f-price--on-sale',
          soldOut: 'f-price--sold-out',
        };
        const selectors = {
          priceWrapper: '.f-price',
          salePrice: '.f-price-item--sale',
          compareAtPrice: ['.f-price-item--regular'],
          unitPriceWrapper: '.f-price__unit-wrapper',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { priceWrapper, salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this
        );
        const unitPrice = unitPriceWrapper.querySelector('.f-price__unit');

        // Store original price before updating to track changes
        const originalPriceText = salePrice ? salePrice.innerHTML : '';
        console.debug('[sticky-atc] updatePrice called - original price:', originalPriceText);
        
        // Store selling plan value before price update for later use
        const sellingPlanInput = this.querySelector('[name="selling_plan"]');
        const sellingPlanValue = sellingPlanInput ? sellingPlanInput.value : null;
        console.debug('[sticky-atc] Current selling plan before price update:', sellingPlanValue);

        const { compare_at_price, price, unit_price_measurement } = this.currentVariant;

        // On sale.
        if (compare_at_price && compare_at_price > price) {
          priceWrapper.classList.add(classes.onSale);
        } else {
          priceWrapper.classList.remove(classes.onSale);
        }

        // Sold out.
        if (!this.currentVariant.available) {
          priceWrapper.classList.add(classes.soldOut);
        } else {
          priceWrapper.classList.remove(classes.soldOut);
        }

        if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

        if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat))
          );
        } else {
          compareAtPrice.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        if (unit_price_measurement && unitPrice) {
          unitPriceWrapper.classList.remove('hidden');
          const unitPriceContent = `<span>${FoxTheme.Currency.formatMoney(
            this.currentVariant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>${FoxTheme.Currency.getBaseUnit(this.currentVariant)}</span>`;
          unitPrice.innerHTML = unitPriceContent;
        } else {
          unitPriceWrapper.classList.add('hidden');
        }
        
        // Use requestAnimationFrame to ensure DOM updates complete before checking selling plans
        requestAnimationFrame(() => {
          // Check for selling plan price with a slight delay
          // This ensures Recharge has time to update its inputs
          setTimeout(() => {
            console.debug('[sticky-atc] Checking for selling plan price after regular price update');
            const newPriceText = salePrice ? salePrice.innerHTML : '';
            console.debug('[sticky-atc] Updated regular price:', newPriceText);
            
            // Retrieve the current selling plan value again - it may have changed
            const sellingPlanInput = this.querySelector('[name="selling_plan"]');
            const currentSellingPlanValue = sellingPlanInput ? sellingPlanInput.value : null;
            
            console.debug('[sticky-atc] Selling plan values:', {
              before: sellingPlanValue,
              current: currentSellingPlanValue,
              changed: sellingPlanValue !== currentSellingPlanValue
            });
            
            // Double-check variant ID
            const variantInput = this.querySelector('[name="id"]');
            const variantId = variantInput ? variantInput.value : null;
            
            // Cache the current DOM state to detect if updatePriceFromSellingPlan changes anything
            const priceBeforeUpdate = salePrice ? salePrice.innerHTML : '';
            const priceWrapperClassList = priceWrapper ? priceWrapper.className : '';
            
            // Call updatePriceFromSellingPlan with the current variant and selling plan
            this.updatePriceFromSellingPlan();
            
            // After a delay, verify the price was updated and force if needed
            setTimeout(() => {
              const priceAfterUpdate = salePrice ? salePrice.innerHTML : '';
              const priceWrapperClassListAfter = priceWrapper ? priceWrapper.className : '';
              
              console.debug('[sticky-atc] Price update verification:', {
                priceChanged: priceBeforeUpdate !== priceAfterUpdate,
                beforePrice: priceBeforeUpdate,
                afterPrice: priceAfterUpdate,
                classesChanged: priceWrapperClassList !== priceWrapperClassListAfter,
                beforeClasses: priceWrapperClassList,
                afterClasses: priceWrapperClassListAfter
              });
              
              // If price didn't change but there's a selling plan, something went wrong - force update
              if (priceBeforeUpdate === priceAfterUpdate && currentSellingPlanValue) {
                console.debug('[sticky-atc] Forcing subscription price update for variant:', variantId);
                
                // Try to directly update from allocations data
                if (window.variantSellingPlanAllocations && 
                    window.variantSellingPlanAllocations[variantId] && 
                    window.variantSellingPlanAllocations[variantId].length) {
                  
                  const allocations = window.variantSellingPlanAllocations[variantId];
                  const matchingAllocation = allocations.find(a => 
                    String(a.selling_plan_id) === String(currentSellingPlanValue)
                  );
                  
                  if (matchingAllocation && matchingAllocation.price) {
                    // Directly update the price with correct formatting
                    salePrice.innerHTML = FoxTheme.Currency.formatMoney(matchingAllocation.price, moneyFormat);
                    
                    // Force the on-sale class
                    priceWrapper.classList.add(classes.onSale);
                    
                    console.debug('[sticky-atc] Forcibly updated subscription price:', {
                      price: matchingAllocation.price,
                      formatted: FoxTheme.Currency.formatMoney(matchingAllocation.price, moneyFormat)
                    });
                  }
                }
              }
            }, 200);
          }, 250);
        });
      }

      syncWithMainProductForm() {
        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          const isMainProduct = event.data.sectionId === this.mainProductInfo.dataset.section;
          if (!isMainProduct) return;
          
          console.debug('[sticky-atc] Variant change event received', event.data);
          
          const variant = event.data.variant;
          const variantInput = this.querySelector('[name="id"]');

          this.currentVariant = variant;
          variantInput.value = variant.id;
          this.updatePrice();
          this.updateButton(true, '', false);
          if (!variant) {
            this.updateButton(true, '', true);
          } else {
            this.updateButton(!variant.available, FoxTheme.variantStrings.soldOut);
          }

          this.updateQuantityInput();
          
          // Check for selling plan pricing after a small delay
          // This allows Recharge to update its widget with the new variant
          requestAnimationFrame(() => {
            setTimeout(() => {
              console.debug('[sticky-atc] Checking for selling plan after variant change with extended delay');
              this.updatePriceFromSellingPlan();
            }, 800); // Increased from 300ms to give more time for Recharge initialization
          });
        });
        
        // Listen for selling plan changes
        document.addEventListener('change', (event) => {
          // Check if the change is to a selling_plan input in the main form
          if (event.target.name === 'selling_plan') {
            console.debug('[sticky-atc] Selling plan change detected', {
              value: event.target.value,
              form: event.target.closest('form')?.id || 'unknown'
            });
            
            // Sync selling plan to sticky ATC if not already done
            const mainForm = this.mainProductInfo?.querySelector('form');
            const stickySellingPlanInput = this.querySelector('[name="selling_plan"]');
            
            if (mainForm && event.target.closest('form') === mainForm && stickySellingPlanInput) {
              stickySellingPlanInput.value = event.target.value;
              console.debug('[sticky-atc] Synced selling plan from main form', event.target.value);
            }
            
            // Give a small delay for Recharge to update the main price
            setTimeout(() => {
              this.updatePriceFromSellingPlan();
            }, 300);
          }
        });
        
        // Listen for specific Recharge events if available
        if (window.ReChargeWidget) {
          document.addEventListener('recharge:option-change', (event) => {
            console.debug('[sticky-atc] Recharge option change event detected', event);
            
            // Short delay to allow Recharge to update its inputs
            setTimeout(() => {
              // Check if the selling plan was cleared (switched to one-time purchase)
              const sellingPlanInput = this.querySelector('[name="selling_plan"]');
              const wasSellingPlanRemoved = !sellingPlanInput || !sellingPlanInput.value;
              
              if (wasSellingPlanRemoved) {
                console.debug('[sticky-atc] Detected switch to one-time purchase in Recharge widget');
                // Ensure price display is reset to regular price
                const priceWrapper = this.querySelector('.f-price');
                if (priceWrapper) {
                  priceWrapper.classList.remove('f-price--on-sale');
                }
              }
              
              this.updatePriceFromSellingPlan();
            }, 300);
          });
        }
        
        // Also listen for variant:changed event which variant-cards.js might dispatch
        document.addEventListener('variant:changed', (event) => {
          if (event.detail && event.detail.variant) {
            console.debug('[sticky-atc] variant:changed event detected', event.detail.variant);
            setTimeout(() => this.updatePriceFromSellingPlan(), 300);
          }
        });
      }
      
      /**
       * Observes price changes in the main product form (for Recharge integration)
       * This is a fallback mechanism to ensure sticky ATC price matches what Recharge shows
       */
      observeMainPrice() {
        try {
          // Find the main product price element that Recharge would update
          const mainProductInfo = document.querySelector('.product__info-wrapper');
          if (!mainProductInfo) {
            console.warn('[sticky-atc] Main product info not found for price observer');
            return;
          }
          
          // Try multiple selectors to find the price element
          const possibleSelectors = [
            '.f-price',                   // Standard theme price container
            '.rc_widget__price',          // Recharge price container
            '[data-recharge-price]',      // Custom Recharge price attribute
            '.product-price'              // Generic product price class
          ];
          
          let mainPriceElement = null;
          for (const selector of possibleSelectors) {
            const element = mainProductInfo.querySelector(selector);
            if (element) {
              mainPriceElement = element;
              console.debug(`[sticky-atc] Found price element with selector: ${selector}`);
              break;
            }
          }
          
          if (!mainPriceElement) {
            console.warn('[sticky-atc] Could not find main price element for observation');
            return;
          }
          
          // Also observe Recharge's widget container if present
          const rechargeWidget = document.querySelector('#rc_container, .rc_container');
          if (rechargeWidget) {
            console.debug('[sticky-atc] Found Recharge widget container for observation');
          }
          
          // Don't set up multiple observers
          if (this.priceObserver) {
            this.priceObserver.disconnect();
          }
          
          // Track whether we've recently updated to avoid loops
          let recentlyUpdated = false;
          
          // Create observer to watch for price changes in the main form
          this.priceObserver = new MutationObserver((mutations) => {
            // Skip if we recently updated to avoid feedback loops
            if (recentlyUpdated) {
              recentlyUpdated = false;
              return;
            }
            
            // Look for price changes in mutations
            let priceChanged = false;
            for (const mutation of mutations) {
              if (mutation.type === 'childList' || mutation.type === 'characterData') {
                priceChanged = true;
                break;
              }
            }
            
            if (!priceChanged) return;
            
            // Find the sale price element in main form
            const mainSalePriceElement = mainPriceElement.querySelector('.f-price-item--sale, .rc_price');
            if (!mainSalePriceElement) {
              console.debug('[sticky-atc] No sale price element found in main form after mutation');
              return;
            }
            
            // Get the current price text from the main product form
            const currentDisplayedPrice = mainSalePriceElement.textContent.trim();
            
            // Update the sticky ATC price to match
            const stickySalePriceElement = this.querySelector('.f-price-item--sale');
            if (stickySalePriceElement && currentDisplayedPrice) {
              // Only update if we have a valid price and not already matching
              if (stickySalePriceElement.textContent.trim() !== currentDisplayedPrice) {
                console.debug('[sticky-atc] Syncing price from main form:', currentDisplayedPrice);
                
                // Mark as recently updated to prevent observer loops
                recentlyUpdated = true;
                
                stickySalePriceElement.innerHTML = currentDisplayedPrice;
                
                // Also check for compare-at price
                const mainComparePrice = mainPriceElement.querySelector('.f-price-item--regular, .rc_compare_price');
                const stickyComparePrice = this.querySelector('.f-price-item--regular');
                
                if (mainComparePrice && stickyComparePrice) {
                  stickyComparePrice.innerHTML = mainComparePrice.innerHTML;
                }
              }
            }
          });
          
          // Observe the price element and its subtree for changes
          this.priceObserver.observe(mainPriceElement, {
            subtree: true,
            childList: true,
            characterData: true,
            attributes: true,
            attributeFilter: ['class', 'data-price']
          });
          
          // Also observe Recharge widget if found
          if (rechargeWidget) {
            this.priceObserver.observe(rechargeWidget, {
              subtree: true,
              childList: true,
              characterData: true
            });
          }
          
          console.debug('[sticky-atc] Main product price observer set up successfully');
          
          // Force check once after setup with a delay
          setTimeout(() => {
            const mainSalePriceElement = mainPriceElement.querySelector('.f-price-item--sale, .rc_price');
            const stickySalePriceElement = this.querySelector('.f-price-item--sale');
            
            if (mainSalePriceElement && stickySalePriceElement) {
              const mainPrice = mainSalePriceElement.textContent.trim();
              console.debug('[sticky-atc] Initial price check - main price:', mainPrice);
              
              if (mainPrice && stickySalePriceElement.textContent.trim() !== mainPrice) {
                stickySalePriceElement.innerHTML = mainPrice;
                console.debug('[sticky-atc] Updated sticky price from initial check');
              }
            }
          }, 1000);
        } catch (e) {
          console.error('[sticky-atc] Error setting up price observer:', e);
        }
      }
      
      /**
       * Updates sticky ATC price based on selected selling plan allocation
       * This is the data-driven approach to ensure correct subscription pricing
       */
      updatePriceFromSellingPlan() {
        try {
          // Get the current variant and selling plan
          const variantId = this.querySelector('[name="id"]')?.value;
          const sellingPlanInput = this.querySelector('[name="selling_plan"]');
          
          console.debug('[sticky-atc] updatePriceFromSellingPlan called', {
            variantId,
            sellingPlanInput: sellingPlanInput ? 'found' : 'not found',
            sellingPlanValue: sellingPlanInput?.value || 'empty',
            timestamp: new Date().toISOString().split('T')[1]
          });
          
          // If no selling plan input or no value, reset to regular price
          if (!sellingPlanInput || !sellingPlanInput.value) {
            console.debug('[sticky-atc] No selling plan selected, resetting to regular price');
            
            // Reset to regular variant price (no discount)
            const moneyFormat = FoxTheme.settings.moneyFormat;
            const priceWrapper = this.querySelector('.f-price');
            const salePrice = this.querySelector('.f-price-item--sale');
            const compareAtPriceElements = this.querySelectorAll('.f-price-item--regular');
            
            // If we have the current variant, check if it's on sale (without subscription)
            if (this.currentVariant) {
              const variantPrice = this.currentVariant.price;
              const variantCompareAtPrice = this.currentVariant.compare_at_price;
              
              // Only show sale price display if there's a regular compare-at-price discount
              if (variantCompareAtPrice && variantCompareAtPrice > variantPrice) {
                if (priceWrapper) {
                  priceWrapper.classList.add('f-price--on-sale');
                  console.debug('[sticky-atc] Added on-sale class (variant has compare-at-price discount)');
                }
                
                // Update compare-at price displays
                if (compareAtPriceElements && compareAtPriceElements.length) {
                  const formattedComparePrice = FoxTheme.Currency.formatMoney(variantCompareAtPrice, moneyFormat);
                  compareAtPriceElements.forEach(element => {
                    element.innerHTML = formattedComparePrice;
                  });
                }
              } else {
                // Remove on-sale class when there's no regular discount
                if (priceWrapper) {
                  priceWrapper.classList.remove('f-price--on-sale');
                  console.debug('[sticky-atc] Removed on-sale class (variant has no compare-at-price discount)');
                }
              }
              
              // Always update the price display with the current variant price
              if (salePrice) {
                const regularPrice = FoxTheme.Currency.formatMoney(variantPrice, moneyFormat);
                salePrice.innerHTML = regularPrice;
                console.debug('[sticky-atc] Reset to regular price:', regularPrice);
              }
            }
            
            return;
          }
          
          const sellingPlanId = sellingPlanInput.value;
          
          // Debug logs for troubleshooting
          console.debug('[sticky-atc] variantId:', variantId);
          console.debug('[sticky-atc] sellingPlanId:', sellingPlanId);
          console.debug('[sticky-atc] window.variantSellingPlanAllocations available:', window.variantSellingPlanAllocations ? 'yes' : 'no');
          
          // Refresh selling plan data if needed
          if (!window.variantSellingPlanAllocations) {
            this.ensureSellingPlanData();
          }
          
          // Check if we have access to the variant selling plan allocations
          if (!window.variantSellingPlanAllocations) {
            console.warn('[sticky-atc] window.variantSellingPlanAllocations is not defined');
            return;
          }
          
          if (!window.variantSellingPlanAllocations[variantId]) {
            console.warn('[sticky-atc] No selling plan allocations found for variant:', variantId);
            console.debug('[sticky-atc] Available variant IDs:', Object.keys(window.variantSellingPlanAllocations));
            return;
          }
          
          // Find the matching selling plan allocation
          const allocations = window.variantSellingPlanAllocations[variantId];
          console.debug('[sticky-atc] Available allocations for variant:', allocations);
          
          // Using string comparison to avoid type mismatch with large numbers
          const matchingAllocation = allocations.find(a => String(a.selling_plan_id) === String(sellingPlanId));
          
          if (!matchingAllocation) {
            console.warn('[sticky-atc] No matching allocation found for selling plan:', sellingPlanId);
            console.debug('[sticky-atc] Selling plan types:', {
              sellingPlanId: typeof sellingPlanId + ' (' + sellingPlanId + ')',
              allocations: allocations.map(a => typeof a.selling_plan_id + ' (' + a.selling_plan_id + ')')
            });
            return;
          }
          
          console.debug('[sticky-atc] Found matching allocation:', matchingAllocation);
          
          // Get the price elements and format settings
          const selectors = {
            priceWrapper: '.f-price',
            salePrice: '.f-price-item--sale',
            compareAtPrice: ['.f-price-item--regular']
          };
          
          const { priceWrapper, salePrice, compareAtPrice } = FoxTheme.utils.queryDomNodes(
            selectors,
            this
          );
          
          // Add f-price--on-sale class to ensure price is visible
          if (priceWrapper) {
            priceWrapper.classList.add('f-price--on-sale');
          }
          
          // Update the price with the discounted subscription price
          if (salePrice && matchingAllocation.price) {
            const moneyFormat = FoxTheme.settings.moneyFormat;
            const formattedPrice = FoxTheme.Currency.formatMoney(matchingAllocation.price, moneyFormat);
            
            // Store the original price for debugging
            const originalPrice = salePrice.innerHTML;
            
            // Update the price element
            salePrice.innerHTML = formattedPrice;
            
            console.debug('[sticky-atc] Updated price from selling plan allocation:', {
              originalPrice: originalPrice,
              newPrice: formattedPrice,
              originalVariantPrice: this.currentVariant?.price,
              discountedPrice: matchingAllocation.price,
              timestamp: new Date().toISOString().split('T')[1]
            });
            
            // Update compare at price if present
            if (compareAtPrice && compareAtPrice.length && matchingAllocation.compare_at_price) {
              compareAtPrice.forEach(element => {
                element.innerHTML = FoxTheme.Currency.formatMoney(matchingAllocation.compare_at_price, moneyFormat);
              });
            }
          } else {
            console.warn('[sticky-atc] Could not update price - missing element or price data', {
              salePrice: salePrice ? 'found' : 'not found',
              matchingAllocationPrice: matchingAllocation.price
            });
          }
          
          // Add a MutationObserver to detect if something is overriding our price update
          // Use a single global observer to avoid creating multiple observers
          if (salePrice && !this._priceWatcher) {
            this._lastKnownCorrectPrice = {
              price: matchingAllocation.price,
              sellingPlanId: sellingPlanId,
              variantId: variantId
            };
            
            this._priceWatcher = new MutationObserver((mutations) => {
              // If we don't have current correct price data, don't try to enforce anything
              if (!this._lastKnownCorrectPrice) return;
              
              // Check if we're still on the same variant and selling plan
              const currentVariantId = this.querySelector('[name="id"]')?.value;
              const currentSellingPlanId = this.querySelector('[name="selling_plan"]')?.value;
              
              if (currentVariantId !== this._lastKnownCorrectPrice.variantId || 
                  currentSellingPlanId !== this._lastKnownCorrectPrice.sellingPlanId) {
                console.debug('[sticky-atc] Variant or selling plan changed, not enforcing price');
                return;
              }
              
              mutations.forEach(mutation => {
                console.debug('[sticky-atc] Price element mutation detected:', {
                  type: mutation.type,
                  target: mutation.target.tagName,
                  currentContent: salePrice.innerHTML,
                  timestamp: new Date().toISOString().split('T')[1]
                });
                
                // Check if price was changed to something other than our expected value
                const moneyFormat = FoxTheme.settings.moneyFormat;
                const expectedPrice = FoxTheme.Currency.formatMoney(this._lastKnownCorrectPrice.price, moneyFormat);
                
                if (salePrice.innerHTML !== expectedPrice) {
                  console.debug('[sticky-atc] Price was overridden, re-applying correct price:', {
                    current: salePrice.innerHTML,
                    expected: expectedPrice
                  });
                  
                  // Re-apply correct price
                  salePrice.innerHTML = expectedPrice;
                  
                  // Ensure price wrapper still has on-sale class
                  if (priceWrapper) {
                    priceWrapper.classList.add('f-price--on-sale');
                  }
                }
              });
            });
            
            // Start observing with all relevant options
            this._priceWatcher.observe(salePrice, {
              characterData: true,
              childList: true,
              subtree: true,
              attributes: false
            });
            
            // Also observe the wrapper for class changes
            if (priceWrapper) {
              this._priceWatcher.observe(priceWrapper, {
                attributes: true,
                attributeFilter: ['class']
              });
            }
            
            console.debug('[sticky-atc] Set up price element watcher to enforce subscription pricing');
          } else if (salePrice && this._priceWatcher) {
            // Update the last known correct price data
            this._lastKnownCorrectPrice = {
              price: matchingAllocation.price,
              sellingPlanId: sellingPlanId,
              variantId: variantId
            };
            
            console.debug('[sticky-atc] Updated price watcher with new correct price data');
          }
        } catch (e) {
          console.error('[sticky-atc-bar] Error updating price from selling plan:', e);
        }
      }
    }
  );
}

// Add a debug listener to check if variant-selling-plan-allocations is available
document.addEventListener('DOMContentLoaded', () => {
  console.debug('[sticky-atc] Checking for variant-selling-plan-allocations data');
  const sellingPlanDataElement = document.getElementById('variant-selling-plan-allocations');
  
  if (!sellingPlanDataElement) {
    console.warn('[sticky-atc] Missing #variant-selling-plan-allocations element - subscription discounts may not show correctly');
    
    // Check if the data is available in window object anyway
    if (window.variantSellingPlanAllocations) {
      console.debug('[sticky-atc] window.variantSellingPlanAllocations is available despite missing element');
    } else {
      console.error('[sticky-atc] window.variantSellingPlanAllocations is NOT available - subscription pricing will not work');
    }
  } else {
    console.debug('[sticky-atc] Found #variant-selling-plan-allocations element');
    
    // Validate the data structure
    try {
      const data = JSON.parse(sellingPlanDataElement.textContent);
      const variantCount = Object.keys(data).length;
      console.debug(`[sticky-atc] Parsed selling plan data for ${variantCount} variants`);
      
      // Check if the first variant has allocations
      const firstVariantId = Object.keys(data)[0];
      if (firstVariantId) {
        const firstVariantAllocations = data[firstVariantId];
        console.debug(`[sticky-atc] First variant (${firstVariantId}) has ${firstVariantAllocations.length} selling plan allocations`);
      }
    } catch (e) {
      console.error('[sticky-atc] Error parsing variant-selling-plan-allocations:', e);
    }
  }
});
